/**
 * Pre-Authentication Version Guard
 * Uses Firebase Remote Config (no auth required) for basic version checking
 * before user logs in. This guard does NOT use Firestore.
 */

import { Capacitor } from '@capacitor/core';
import React, { useState, useEffect, useCallback } from 'react';
import { getAll, getValue, fetchAndActivate } from 'firebase/remote-config';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useNetworkStatus } from 'src/hooks/use-network-status';

import { getAppVersion, isAppVersionValid } from 'src/utils/guards/version-utils';

import { useSystemStore } from 'src/store/SystemStore';
import { useApplicationFlags } from 'src/store/AppFlags';

import { SplashScreen } from 'src/components/loading-screen';

import { AppNameEnum } from 'src/sections/app-hub/types';

import { firebaseRemoteConfig } from './minimum-version-guard';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

/**
 * Pre-Auth Version Guard
 * - Uses Firebase Remote Config (doesn't require authentication)
 * - Checks basic minimum version before login
 * - Allows access to login and update pages
 * - Does NOT access Firestore
 */
export default function PreAuthVersionGuard({ children }: Props) {
  const [loading, setLoading] = useState(true);
  const [appVersionIsValid, setAppVersionIsValid] = useState(false);
  const [hasCheckedVersion, setHasCheckedVersion] = useState(false);
  const { setAllFlags } = useApplicationFlags();
  const { setBaseUrl, setMinimumAppVersion, setBaseAthUrl, setAppsToDisplayOnMobile } = useSystemStore();
  const { isOnline } = useNetworkStatus();
  const router = useRouter();

  // Skip all version checks on non-native platforms
  const isNativePlatform = Capacitor.isNativePlatform();
  const platform = Capacitor.getPlatform();

  // Early return for web/desktop platforms - no update checks needed
  useEffect(() => {
    if (!isNativePlatform) {
      console.log(`[PreAuthVersionGuard] Platform ${platform} detected - skipping update checks`);
      setLoading(false);
      setAppVersionIsValid(true);
      setHasCheckedVersion(true);
      setMinimumAppVersion('0.0.0');
    }
  }, [isNativePlatform, platform, setMinimumAppVersion]);

  // Handle offline scenario
  const handleOfflineFlow = useCallback(async () => {
    console.log('[PreAuthVersionGuard] Device is offline, skipping remote config fetch');
    try {
      // Get current app version without remote config
      const currentAppVersion = await getAppVersion();
      if (!currentAppVersion) {
        console.log('[PreAuthVersionGuard] ERROR: Could not get app version');
        // When offline and can't get app version, allow through to avoid blocking
        setAppVersionIsValid(true);
        setLoading(false);
        return;
      }

      // When offline, use default values and allow through
      // We'll re-check when connection is restored
      setMinimumAppVersion(firebaseRemoteConfig.defaultConfig.minimum_app_version as string);
      setAppVersionIsValid(true); // Allow through when offline
      console.log(
        '[PreAuthVersionGuard] Offline mode: Allowing app to continue with version:',
        currentAppVersion
      );
    } catch (error) {
      console.error('[PreAuthVersionGuard] Error in offline flow:', error);
      // Even if offline flow fails, allow through to avoid blocking
      setAppVersionIsValid(true);
    } finally {
      setLoading(false);
      setHasCheckedVersion(true);
    }
  }, [setMinimumAppVersion]);

  // Handle online version checking using Remote Config (no auth required)
  const checkVersionOnline = useCallback(async () => {
    console.log('[PreAuthVersionGuard] Fetching remote config (online)');
    try {
      const activated = await fetchAndActivate(firebaseRemoteConfig);
      if (activated) {
        console.log('[PreAuthVersionGuard] Remote config activated');
      } else {
        // Check if values exist
        const allValues = await getAll(firebaseRemoteConfig);
        console.log('[PreAuthVersionGuard] Current application Flags (Cloud):', allValues);
        setAllFlags({
          minimum_app_version: allValues.minimum_app_version.asString(),
          show_coming_soon_apps: allValues.show_coming_soon_apps?.asBoolean() || false,
          elevate_ai_enabled: allValues.elevate_ai_enabled?.asBoolean() || false,
          marketplace_v2_flag: allValues.marketplace_v2_flag?.asBoolean() || false,
          manager_v2_app_release: allValues.manager_v2_app_release?.asBoolean() ?? true,
          mobile_pay_tab_feature: allValues.MOBILE_PAY_TAB_FEATURE?.asBoolean() ?? false,
          enable_terminal_presence_wake_check:
            allValues.enable_terminal_presence_wake_check?.asBoolean() ?? true,
        });
        if (allValues?.custom_base_url?.asString()) {
          setBaseUrl(allValues.custom_base_url.asString());
        }
      }

      // Base URL
      const baseUrl = getValue(firebaseRemoteConfig, 'custom_base_url');
      const baseAthUrl = getValue(firebaseRemoteConfig, 'custom_base_ath_url');
      const appsToDisplayOnMobile = getValue(firebaseRemoteConfig, 'apps_to_display_on_mobile');

      if (baseUrl.asString()) {
        setBaseUrl(baseUrl.asString());
      }
      if (baseAthUrl.asString()) {
        setBaseAthUrl(baseAthUrl.asString());
      }
      if (appsToDisplayOnMobile.asString()) {
        try {
          setAppsToDisplayOnMobile(JSON.parse(appsToDisplayOnMobile.asString()) as AppNameEnum[]);
        } catch (error) {
          console.error('[PreAuthVersionGuard] Error parsing apps to display on mobile:', error);
        }
      }

      const minimumAppVersion = getValue(firebaseRemoteConfig, 'minimum_app_version');
      console.log('[PreAuthVersionGuard] Minimum App Version:', minimumAppVersion.asString());

      const currentAppVersion = await getAppVersion();
      if (!currentAppVersion) {
        console.log('[PreAuthVersionGuard] ERROR: App Version result:', currentAppVersion);
        throw new Error('App Version is not valid');
      }

      const isValid = isAppVersionValid(currentAppVersion, minimumAppVersion.asString());

      console.log('[PreAuthVersionGuard] Current App Version:', currentAppVersion);
      console.log('[PreAuthVersionGuard] Minimum App Version:', minimumAppVersion.asString());
      console.log('[PreAuthVersionGuard] Is Valid:', isValid);

      setMinimumAppVersion(minimumAppVersion.asString());
      setAppVersionIsValid(isValid);
    } catch (error) {
      console.error('[PreAuthVersionGuard] Error getting app version:', error);
      setAppVersionIsValid(false);
    } finally {
      setLoading(false);
      setHasCheckedVersion(true);
    }
  }, [setAllFlags, setMinimumAppVersion, setBaseUrl, setBaseAthUrl, setAppsToDisplayOnMobile]);

  // Initial version check effect
  useEffect(() => {
    if (!isNativePlatform) {
      return;
    }

    if (!hasCheckedVersion) {
      if (isOnline) {
        checkVersionOnline();
      } else {
        handleOfflineFlow();
      }
    }
  }, [isOnline, hasCheckedVersion, checkVersionOnline, handleOfflineFlow, isNativePlatform]);

  // Handle reconnection - re-check version when coming back online
  useEffect(() => {
    if (!isNativePlatform) {
      return;
    }

    if (isOnline && hasCheckedVersion) {
      console.log('[PreAuthVersionGuard] Connection restored, re-checking app version');
      setLoading(true);
      checkVersionOnline();
    }
  }, [isOnline, hasCheckedVersion, checkVersionOnline, isNativePlatform]);

  // Navigate to update page if version is invalid
  useEffect(() => {
    if (!loading && !appVersionIsValid && hasCheckedVersion) {
      console.log('[PreAuthVersionGuard] Version invalid, redirecting to update page');
      router.replace(paths.system.updateVersion);
    }
  }, [loading, appVersionIsValid, hasCheckedVersion, router]);

  // On non-native platforms, never show loading screen
  if (!isNativePlatform) {
    return <>{children}</>;
  }

  // Show loading screen while checking
  if (loading) {
    return <SplashScreen />;
  }

  // If version is valid, show children
  // If version is invalid, the redirect effect will handle navigation
  return <>{children}</>;
}
